import * as React from "react";
import {
  AppShell,
  Tooltip,
  UnstyledButton,
  Center,
  Stack,
  Select,
  Title,
  Menu,
  ActionIcon,
  Text,
  Group,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import { IconChecklist, IconLogout, IconUserCircle } from "@tabler/icons-react";
import { useLocalStorage } from "@mantine/hooks";
import { useNavigate, createSearchParams, useMatch, useLocation, Navigate } from "react-router-dom";

import appContainerStyles from "./AppContainer.module.css";

import { getLocalStorage, signOut } from "../../api";
import { getDashboardActions, getRoleAccessOptions, isActionAllowed } from "../../utilities";
import { UserContext } from "../../misc/UserContext";
import { styles } from "../../styles";
import { AccessOptionsType, AccessOptionSubType } from "../../types";
import { useUserAllowedActions } from "../../hooks";

interface IAppContainerProps {
  component: React.ComponentType<{ type?: string }>;
  action: string | string[];
  validate?: boolean;
  header?: string;
  type?: string;
}

function getEntityLOV(userRoles: any) {
  const entityLOV: { label: string; value: string }[] = [];

  for (const role of userRoles) {
    if (entityLOV.findIndex((entity) => entity.value === role.entityId) === -1) {
      entityLOV.push({
        value: role.entityId,
        label: role.entityName,
      });
    }
  }

  return entityLOV;
}

function sortOptions(a: { order: number }, b: { order: number }) {
  if (a.order < b.order) {
    return -1;
  }
  if (a.order > b.order) {
    return 1;
  }
  return 0;
}

function getNavLinks(options: AccessOptionsType) {
  const links = [];

  for (const option of options.sort(sortOptions)) {
    if ("link" in option) {
      links.push(
        <NavbarLink
          icon={option.icon}
          link={option.link}
          label={option.label}
          key={option.label}
          // active={index === active}
          // onClick={() => setActive(index)}
        />,
      );
    } else {
      links.push(
        <NavbarMenu
          icon={option.icon}
          subMenus={option.subMenus}
          label={option.label}
          key={option.label}
          // active={index === active}
          // onClick={() => setActive(index)}
        />,
      );
    }
  }

  return links;
}

function NavbarLink({ icon: Icon, label, link }: { icon: any; label: string; link: string }) {
  let navigate = useNavigate();
  const active = useMatch({ path: `/app/${label.toLowerCase()}` });
  const viewActive = useMatch({
    path: `/app/${label.toLowerCase()}/view`,
  });
  const editActive = useMatch({
    path: `/app/${label.toLowerCase()}/edit`,
  });
  const newActive = useMatch({
    path: `/app/${label.toLowerCase()}/new`,
  });

  return (
    <Tooltip label={label} position="right">
      <UnstyledButton
        className={appContainerStyles.root}
        onClick={() => {
          navigate(link);
        }}
        style={active || viewActive || editActive || newActive ? { ...styles.link, color: "#439CEA" } : styles.link}
      >
        <Icon stroke={active || viewActive || editActive || newActive ? 1.75 : 1.5} />
      </UnstyledButton>
    </Tooltip>
  );
}

function NavbarMenu({
  icon: Icon,
  label,
  subMenus,
}: {
  icon: any;
  label: string;
  subMenus: AccessOptionSubType["subMenus"];
}) {
  let navigate = useNavigate();
  const location = useLocation();
  const active = subMenus.some((menu) => location.pathname.indexOf(menu.label.toLowerCase()) !== -1);
  return (
    <Menu position="right-start" offset={20}>
      <Menu.Target>
        <Tooltip label={label} position="right">
          <UnstyledButton
            className={appContainerStyles.root}
            style={active ? { ...styles.link, color: "#439CEA" } : styles.link}
          >
            <Icon stroke={active ? 1.75 : 1.5} />
          </UnstyledButton>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        {subMenus.sort(sortOptions).map((menu) => {
          if ("link" in menu) {
            return (
              <Menu.Item
                key={menu.label}
                leftSection={<menu.icon size={16} />}
                style={{ width: "200px" }}
                onClick={() => {
                  navigate(menu.link);
                }}
              >
                {menu.label}
              </Menu.Item>
            );
          }
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

export default function AppContainer({
  component: Component,
  action,
  validate = true,
  header,
  ...rest
}: IAppContainerProps) {
  const [currentEntity, setCurrentEntity] = useLocalStorage({
    key: "currentEntity",
    defaultValue: getLocalStorage("currentEntity"),
  });

  const [user] = useLocalStorage({
    key: "user",
    defaultValue: { userId: "", userName: "" },
  });

  const [userRoles] = useLocalStorage({
    key: "roles",
    defaultValue: getLocalStorage("roles"),
  });

  let navigate = useNavigate();

  const { data: userAllowedActions } = useUserAllowedActions(currentEntity);

  const links = getNavLinks(getDashboardActions(currentEntity, userAllowedActions));
  // const links = getNavLinks(getRoleAccessOptions(currentEntity, userRoles));

  const theme = useMantineTheme();

  // Check only for routes where validate is set to true. Default value is true(validate access control).
  // Validation will be skipped for default access screens such as dashboard, tasks
  if (validate) {
    if (!isActionAllowed(action, userAllowedActions)) {
      console.log("No access to action");
      // return <Navigate to="/app/dashboard" replace={true} />;
    }
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 80, breakpoint: "sm", collapsed: { mobile: false } }}
      padding="md"
      style={{ backgroundColor: "#F5F7F8" }}
    >
      <AppShell.Navbar py="md" h={"100vh"} w={{ base: 80 }} style={{ zIndex: 101, top: 0 }}>
        <Center>
          <IconChecklist size={40} strokeWidth={1.75} color={"teal"} />
        </Center>

        <Stack justify="center" align="center" gap={0} pt={"xl"}>
          {links}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Header h={67} py={theme.spacing.md} pl={theme.spacing.xxxl} w="100vw">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Title
            order={2}
            py={0}
            px={theme.spacing.xxs}
            c="white"
            style={{
              // marginLeft: "80px",
              backgroundColor: theme.colors.emerald[0],
            }}
          >
            {import.meta.env.VITE_APP_BRAND}
          </Title>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexGrow: 2,
              paddingRight: theme.spacing.xl,
            }}
          >
            <Select
              mr={theme.spacing.md}
              value={currentEntity}
              onChange={(val) => {
                if (val) {
                  setCurrentEntity(val);
                  navigate({
                    pathname: "/app/dashboard",
                    search: createSearchParams({
                      entityId: val,
                    }).toString(),
                  });
                }
                //   navigate("/app/dashboard");
              }}
              data={getEntityLOV(userRoles)}
            />

            <Menu position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="transparent" c="gray.6" aria-label="Profile" style={{ outline: "none" }}>
                  <IconUserCircle />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown style={{ minWidth: "250px" }} c="gray.9">
                <Menu.Item onClick={() => signOut()}>
                  <Group justify="flex-start">
                    <IconLogout color="#585D60" />
                    <Text>Logout</Text>
                  </Group>
                </Menu.Item>
                <Divider />
                <Menu.Item c="gray.9">
                  <Text fw={500}>{user.userName}</Text>
                  <Text c="dimmed">{user.userId}</Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>
      </AppShell.Header>

      <AppShell.Main>
        <Component {...rest} />
      </AppShell.Main>
    </AppShell>
  );
}
